<template>
  <div class="relative">
    <RichText
      v-if="PaymentMethodTitle?.value"
      :field="PaymentMethodTitle"
      class="text-racq-dark-grey text-xl-bold mb-6"
    />
    <div v-if="isMasked" class="absolute inset-x-0 inset-y-0 bg-curtain-color z-[100]"></div>
    <div class="payment-method-container flex flex-col divide-y">
      <!-- FZ IFRAME START -->
      <div v-if="acceptCardPayment">
        <div
          class="card-type-section flex space-between items-center py-4"
          :class="paymentTypeSelectable && 'hover:bg-racq-white-background cursor-pointer'"
          @click="() => changePaymentMethod(PaymentTypes.CreditCard)"
        >
          <FormKit
            v-if="paymentTypeSelectable"
            v-model="selectedPaymentType"
            type="nativeRadio"
            name="paymentTypeSelection"
            outerClass="-mt-1"
            :options="[{ label: 'CreditCard', value: PaymentTypes.CreditCard, srOnly: true }]"
          ></FormKit>
          <RichText
            v-if="CardTypeTitle?.value"
            :field="CardTypeTitle"
            class="card-type-title text-base-bold text-racq-dark-grey"
          />

          <RichText
            v-if="ShowCardIcons?.value && CardIcons?.value"
            class="card-type-icons flex ml-auto gap-1 h-6"
            :field="CardIcons"
          />
        </div>
      </div>
      <!-- FZ IFRAME START -->

      <!-- BANK ACCOUNT START -->
      <div v-if="acceptBankAccount">
        <div
          class="card-type-section flex space-between items-center py-4"
          :class="paymentTypeSelectable && 'hover:bg-racq-white-background cursor-pointer'"
          @click="() => changePaymentMethod(PaymentTypes.BankAccount)"
        >
          <FormKit
            v-if="paymentTypeSelectable"
            v-model="selectedPaymentType"
            type="nativeRadio"
            name="paymentTypeSelection"
            outerClass="-mt-1"
            :options="[{ label: 'BankAccount', value: PaymentTypes.BankAccount, srOnly: true }]"
          ></FormKit>
          <RichText
            v-if="BankAccountTitle?.value"
            :field="BankAccountTitle"
            class="card-type-title text-base-bold text-racq-dark-grey"
          />
          <RichText
            v-if="BankAccountIcon?.value"
            class="card-type-icons flex ml-auto gap-2 h-6"
            :field="BankAccountIcon"
          />
        </div>
      </div>
      <!-- BANK ACCOUNT END -->
      <!-- GOOGLE PAY START -->
      <div
        v-if="acceptGooglePay"
        class="card-type-section flex space-between items-center py-4"
        :class="paymentTypeSelectable && 'hover:bg-racq-white-background cursor-pointer'"
        @click="() => changePaymentMethod(PaymentTypes.GooglePay)"
      >
        <FormKit
          v-if="paymentTypeSelectable"
          v-model="selectedPaymentType"
          type="nativeRadio"
          name="paymentTypeSelection"
          outerClass="-mt-1"
          :options="[{ label: 'GooglePay', value: PaymentTypes.GooglePay, srOnly: true }]"
        ></FormKit>
        <span class="card-type-title text-base-bold text-racq-dark-grey">Google Pay</span>
        <RichText v-if="GooglePayIcon?.value" class="card-type-icons flex ml-auto gap-1 h-6" :field="GooglePayIcon" />
      </div>
      <!-- GOOGLE PAY END -->

      <!-- APPLE PAY START -->
      <div
        v-if="acceptApplePay"
        class="card-type-section flex space-between items-center py-4"
        :class="paymentTypeSelectable && 'hover:bg-racq-white-background cursor-pointer'"
        @click="() => changePaymentMethod(PaymentTypes.ApplePay)"
      >
        <FormKit
          v-if="paymentTypeSelectable"
          v-model="selectedPaymentType"
          type="nativeRadio"
          name="paymentTypeSelection"
          outerClass="-mt-1"
          :options="[{ label: 'ApplePay', value: PaymentTypes.ApplePay, srOnly: true }]"
        ></FormKit>
        <span class="card-type-title text-base-bold text-racq-dark-grey">Apple Pay</span>
        <RichText v-if="ApplePayIcon?.value" class="card-type-icons flex ml-auto gap-1 h-6" :field="ApplePayIcon" />
      </div>
      <!-- APPLE PAY END -->

      <!-- PAYMENT INPUTS START -->
      <div v-show="selectedPaymentType" class="border-t">
        <div
          v-if="acceptCardPayment"
          class="iframe-section ease-in-out duration-500 transition-all my-8 max-h-[1000px]"
          :class="selectedPaymentType != PaymentTypes.CreditCard && '!overflow-hidden !max-h-[0px] !my-0 opacity-0'"
        >
          <slot>
            <PaymentSectionFZIframe />
          </slot>
          <RichText
            v-if="PaymentDisclaimer?.value"
            :field="PaymentDisclaimer"
            class="disclaimer-section flex flex-col text-sm font-normal"
          />
        </div>

        <div
          v-if="
            acceptBankAccount &&
            selectedPaymentType == PaymentTypes.BankAccount &&
            (!paymentStore.applyTermsAndConditionsValidationFix || paymentStore.termsAndConditionsAccepted)
          "
          class="ease-in-out duration-500 transition-all my-8 px-3 max-h-[1000px]"
          :class="selectedPaymentType != PaymentTypes.BankAccount && '!overflow-hidden !max-h-[0px] !my-0 opacity-0'"
        >
          <FormKit type="group" name="payment-section-bank-account">
            <template #default="{ state: { valid } }">
              <FormKit
                id="bank-account-account-name"
                ref="bankAccountName"
                type="text"
                :label="props.AccountNameLabel?.value"
                outerClass="mb-6"
                innerClass="input-md"
                name="bank-account-account-name"
                :tooltip="{
                  content: props.AccountNameTooltip?.value,
                  tooltipId: 'bank-account-account-name__tooltip_id',
                  tooltipOuterId: 'bank-account-account-name__outer',
                }"
                :validation="[
                  ['required', 'trim'],
                  ['matches', /^[a-zA-Z0-9 -]{1,100}$/],
                ]"
                :validationMessages="{
                  required: props.AccountNameRequiredMessage?.value,
                  matches: props.AccountNameInvalidMessage?.value,
                }"
              />
              <FormKit
                id="bank-account-bsb"
                ref="bsbInput"
                type="text"
                oninput="window.RACQLintBSBInput(this)"
                :label="props.BsbLabel?.value"
                outerClass="mb-4"
                innerClass="input-xs"
                name="bank-account-bsb"
                :help="financialInstitutionName"
                :loading="checkingBsb"
                :validation="[['required', 'trim'], ['matches', /^\d{3}-\d{3}$/], ['bsbLookup']]"
                :validationRules="{ bsbLookup }"
                :validationMessages="{
                  required: props.BsbRequiredMessage?.value,
                  matches: props.BsbInvalidMessage?.value,
                  bsbLookup: props.BsbNotFoundMessage?.value,
                }"
              >
                <template #messages="context">
                  <RichText
                    v-if="context.messages?.rule_bsbLookup?.value"
                    :class="context.classes?.message"
                    :field="{ value: replaceBsb(context.messages?.rule_bsbLookup?.value, context._value) }"
                  />
                  <RichText
                    v-if="context.messages?.rule_required?.value"
                    :class="context.classes?.message"
                    :field="context.messages?.rule_required"
                  />
                  <RichText
                    v-if="context.messages?.rule_matches?.value"
                    :class="context.classes?.message"
                    :field="context.messages?.rule_matches"
                  />
                </template>
              </FormKit>

              <FormKit
                id="bank-account-account-number"
                ref="bankAccountNumber"
                type="tel"
                :label="props.AccountNumberLabel?.value"
                outerClass="mb-4"
                innerClass="input-sm"
                name="bank-account-account-number"
                :validation="[
                  ['required', 'trim'],
                  ['matches', /^(\d{6,9})$/],
                ]"
                :validationMessages="{
                  required: props.AccountNumberRequiredMessage?.value,
                  matches: props.AccountNumberInvalidMessage?.value,
                }"
              />

              <div class="flex w-full pt-4">
                <FormKit
                  type="submit"
                  prefixIcon="lockIcon"
                  prefixIconClass="$remove:-ml-2 $remove:mr-2"
                  :label="SubmitBankAccountLabel?.value"
                  inputClass="btn-fatzebra btn-edit"
                  outerClass="ms-auto"
                  @click="() => valid && submitPayment(PaymentTypes.BankAccount)"
                />
              </div>
              <RichText
                v-if="BankPaymentDisclaimer?.value"
                :field="BankPaymentDisclaimer"
                class="disclaimer-section flex flex-col text-sm font-normal mt-8"
              />
            </template>
          </FormKit>
        </div>

        <div
          v-if="acceptPayLater"
          class="ease-in-out duration-500 transition-all my-8 px-3 max-h-[1000px]"
          :class="selectedPaymentType != PaymentTypes.PayLater && '!overflow-hidden !max-h-[0px] !my-0 opacity-0'"
        >
          <RichText v-if="PayLaterContent?.value" :field="PayLaterContent" />
          <div class="flex w-full pt-4">
            <FormKit
              type="button"
              :label="SubmitPayLaterLabel?.value"
              inputClass="btn btn-secondary input-cta-arrow-right"
              @click="() => submitPayment(PaymentTypes.PayLater)"
            />
          </div>
        </div>

        <div
          v-if="acceptGooglePay"
          :class="selectedPaymentType != PaymentTypes.GooglePay && '!overflow-hidden !max-h-[0px] !my-0 opacity-0'"
        >
          <PaymentSectionGooglePay
            :environment="GOOGLE_PAY_ENVIRONMENT"
            :gatewayMerchantId="GOOGLE_PAY_GATEWAY_MERCHANT_ID"
            :merchantName="GOOGLE_PAY_MERCHANT_NAME"
            :merchantId="GOOGLE_PAY_MERCHANT_ID"
          />
          <RichText
            v-if="PaymentDisclaimer?.value"
            :field="PaymentDisclaimer"
            class="disclaimer-section flex flex-col text-sm font-normal"
          />
        </div>

        <div
          v-if="acceptApplePay"
          :class="selectedPaymentType != PaymentTypes.ApplePay && '!overflow-hidden !max-h-[0px] !my-0 opacity-0'"
        >
          <PaymentSectionApplePay />
          <RichText
            v-if="PaymentDisclaimer?.value"
            :field="PaymentDisclaimer"
            class="disclaimer-section flex flex-col text-sm font-normal"
          />
        </div>
      </div>
      <!-- PAYMENT INPUTS END -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { computed, ref, onMounted, watch } from 'vue';
import PaymentSectionFZIframe from './PaymentSectionFZIframe.vue';
import { usePaymentStore } from '@/store';
import { IPaymentSectionProps } from '@/interfaces';
import { PaymentTypes } from '@/enums/payment-types.enum';
import { FormKit } from '@formkit/vue';
import PaymentSectionGooglePay from './PaymentSectionGooglePay.vue';
import PaymentSectionApplePay from './PaymentSectionApplePay.vue';
import {
  GOOGLE_PAY_ENVIRONMENT,
  GOOGLE_PAY_GATEWAY_MERCHANT_ID,
  GOOGLE_PAY_MERCHANT_ID,
  GOOGLE_PAY_MERCHANT_NAME,
} from '@/constants/urls.const';
import { isMacOS } from '@/utils/dom-helpers';

const props = defineProps<IPaymentSectionProps>();
const paymentStore = usePaymentStore();

const bankAccountName = ref<any>(null);
const bsbInput = ref<any>(null);
const bankAccountNumber = ref<any>(null);
const checkingBsb = ref<boolean>(false);

const acceptCardPayment = computed(() => {
  return props.AcceptPaymentTypes.some((type) => type.name == PaymentTypes[PaymentTypes.CreditCard]);
});

const acceptBankAccount = computed(() => {
  return props.AcceptPaymentTypes.some((type) => type.name == PaymentTypes[PaymentTypes.BankAccount]);
});

const acceptPayLater = computed(() => {
  return props.AcceptPaymentTypes.some((type) => type.name == PaymentTypes[PaymentTypes.PayLater]);
});

const acceptGooglePay = computed(() => {
  return props.AcceptPaymentTypes.some((type) => type.name == PaymentTypes[PaymentTypes.GooglePay]);
});

const acceptApplePay = computed(() => {
  try {
    if (isMacOS() && !props.ApplePayEnableMacOS?.value) {
      return false;
    }
    const supportsApplePay =
      typeof window !== 'undefined' &&
      window.isSecureContext && // ensure HTTPS or localhost with valid cert
      (window as any).ApplePaySession &&
      typeof (window as any).ApplePaySession.canMakePayments === 'function' &&
      (window as any).ApplePaySession.canMakePayments();

    return (
      props.AcceptPaymentTypes.some((type) => type.name === PaymentTypes[PaymentTypes.ApplePay]) &&
      (supportsApplePay || props.ApplePayEnableDesktop?.value)
    );
  } catch (e) {
    console.warn('Apple Pay detection failed:', e);
    return false;
  }
});

const isMasked = computed(() => paymentStore.disablePaymentPanel);

const paymentTypeSelectable = computed(() => {
  return props.AcceptPaymentTypes.length > 0;
});

const selectedPaymentType = computed({
  get: () => paymentStore.selectedPaymentMethod,
  set: (value) => {
    paymentStore.selectedPaymentMethod = value;
  },
});

const financialInstitutionName = computed(() => {
  return paymentStore.bsbBranchDetails?.name
    ? `${paymentStore.bsbBranchDetails?.name}, ${paymentStore.bsbBranchDetails?.branch}`
    : undefined;
});

onMounted(async () => {
  setDefaultPaymentMethod();
});

// Re-evaluate the default when accepted payment types change dynamically (e.g. direct debit yes/no)
watch(
  () => props.AcceptPaymentTypes,
  () => {
    setDefaultPaymentMethod();
  }
);

function setDefaultPaymentMethod() {
  // Only set the default payment method if the user hasn't already made a selection,
  // or if their current selection is no longer available in the accepted types.
  const currentSelection = paymentStore.selectedPaymentMethod;
  const isCurrentSelectionAvailable =
    currentSelection != null && props.AcceptPaymentTypes.some((type) => type.name === PaymentTypes[currentSelection]);

  if (!isCurrentSelectionAvailable && props.AcceptPaymentTypes?.length > 0) {
    paymentStore.selectedPaymentMethod = paymentStore.getDefaultSelectedPaymentMethod(props.AcceptPaymentTypes);
  }
}

const bsbLookup = async function ({ value }: { value: string }) {
  if (value?.length != 7) return;
  checkingBsb.value = true;
  try {
    const bsb = value.replace('-', '');
    const res = await paymentStore.bsbLookup(bsb);
    return res;
  } catch (error) {
    return false;
  } finally {
    checkingBsb.value = false;
  }
};

const replaceBsb = (message: string, bsb: string) => {
  return message?.replace('{{bsb}}', bsb);
};

const submitPayment = async (paymentType: PaymentTypes) => {
  try {
    const bsb = bsbInput.value?.node?.value;
    const accountName = bankAccountName?.value?.node?.value;
    const accountNumber = bankAccountNumber?.value?.node?.value;

    if (bsb && accountName && accountNumber) {
      await paymentStore.setDirectDebitDetails(accountName, bsb, accountNumber);
    }

    paymentStore.submitPaymentCallback && paymentStore.submitPaymentCallback(paymentType);
  } catch (e) {
    console.error(e);
  }
};

/**
 * Change payment method handler
 *
 * @param paymentMethod
 */
const changePaymentMethod = (paymentMethod: PaymentTypes) => {
  if (!paymentTypeSelectable.value) return;
  paymentStore.changePaymentMethod(paymentMethod);

  //Reset iframe otherwise it can be blank sometimes.
  if (paymentMethod === PaymentTypes.CreditCard) {
    paymentStore.resetIframe();
  }
};

// Lint BSB Input
(window as any).RACQLintBSBInput = (target: { value: string }) => {
  try {
    if (!target?.value || typeof target.value != 'string') {
      paymentStore.bsbBranchDetails = null;
      return;
    }
    const bsb = target?.value;
    const bsbAr = bsb.replace(/[^0-9/]/g, '').split('');
    bsbAr.length > 3 && bsbAr.splice(3, 0, '-');
    target.value = bsbAr.join('').slice(0, 7);
    if (!target.value) {
      paymentStore.bsbBranchDetails = null;
    }
  } catch (error) {
    console.warn('Unable to parse bsb: ', error);
  }
};
</script>
